// =====================================================================
// WeddingOS – /guest  (Gast Homescreen)
// =====================================================================
// Vollbild mobile-first:
// • Personalisierter Header (Name, Tisch)
// • 4 große Kacheln füllen den Screen
// • Live-Ankündigungs-Banner via Realtime
// • Kein Menü, kein Burger – jede Kachel ist die Navigation
// =====================================================================

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadGuestSession } from "../../lib/guestSession";
import { fetchAnnouncements, realtime, supabase, guestCanAnnounce } from "../../services/supabase";
import { weddingLocationBranding } from "../../services/locations";
import "../../styles/theme.css";

const TILES = [
  {
    id:    "music",
    path:  "/guest/music",
    icon:  "♩",
    label: "Musik­wunsch",
    hint:  "Wünsch dir einen Song",
    color: "var(--accent)",
    bg:    "linear-gradient(145deg, #F9ECEE 0%, #F2DDD8 100%)",
    border: "var(--accent-soft)",
    premium: true,
  },
  {
    id:    "drinks",
    path:  "/guest/drinks",
    icon:  "◈",
    label: "Getränke",
    hint:  "Bestell direkt am Tisch",
    color: "var(--forest)",
    bg:    "linear-gradient(145deg, #E8F0EA 0%, #DCE6DE 100%)",
    border: "var(--forest-soft)",
    premium: true,
  },
  {
    id:    "photos",
    path:  "/guest/photos",
    icon:  "◻",
    label: "Fotos",
    hint:  "Teile deinen Moment",
    color: "#7A6040",
    bg:    "linear-gradient(145deg, #F5EDE0 0%, #EDE0CC 100%)",
    border: "#E4D4BC",
  },
  {
    id:    "guestbook",
    path:  "/guest/guestbook",
    icon:  "✉",
    label: "Gäste­buch",
    hint:  "Schreib dem Brautpaar",
    color: "#3D5C47",
    bg:    "linear-gradient(145deg, #E3EDE5 0%, #D4E4D8 100%)",
    border: "#C4D8C8",
  },
];

export default function GuestHome() {
  const navigate = useNavigate();
  const session  = loadGuestSession();

  const [announcements, setAnnouncements] = useState([]);
  const [banner,        setBanner]        = useState(null);
  const [bannerKey,     setBannerKey]     = useState(0);
  const [drinksEnabled, setDrinksEnabled] = useState(true);
  const [isPaid,        setIsPaid]        = useState(false);
  const [branding,      setBranding]      = useState(null);
  const [canAnnounce,   setCanAnnounce]   = useState(false);

  useEffect(() => {
    if (!session?.wedding_id) return;
    weddingLocationBranding(session.wedding_id)
      .then((b) => { if (b?.has_branding) setBranding(b); })
      .catch(() => {});
  }, [session?.wedding_id]);

  useEffect(() => {
    if (!session?.wedding_id) return;
    supabase
      .rpc("get_wedding_features", { p_wedding_id: session.wedding_id })
      .then(({ data }) => {
        const f = Array.isArray(data) ? data[0] : data;
        if (f) {
          setDrinksEnabled(f.drinks_enabled !== false);
          setIsPaid(f.is_paid === true);
        }
      })
      .catch(() => {});
  }, [session?.wedding_id]);

  useEffect(() => {
    if (!session?.wedding_id) return;
    fetchAnnouncements(session.wedding_id, 1)
      .then((rows) => setAnnouncements(rows))
      .catch(() => {});
  }, [session?.wedding_id]);

  useEffect(() => {
    if (!session?.guest_id) return;
    guestCanAnnounce(session.guest_id).then(setCanAnnounce).catch(() => {});
  }, [session?.guest_id]);

  useEffect(() => {
    if (!session?.wedding_id) return;
    return realtime.announcements(session.wedding_id, (payload) => {
      if (payload.eventType === "INSERT") {
        setAnnouncements((prev) => [payload.new, ...prev]);
        setBanner(payload.new.message);
        setBannerKey((k) => k + 1);
      }
    });
  }, [session?.wedding_id]);

  if (!session) {
    // tableToken aus sessionStorage nicht mehr vorhanden → zurück zum Join
    // (passiert wenn sessionStorage geleert wurde oder Tab neu geöffnet)
    return (
      <div style={styles.shell}>
        <p style={styles.meta}>Deine Sitzung ist abgelaufen.</p>
        <p style={{ ...styles.meta, fontSize: 13, marginTop: 8, color: "var(--ink-subtle)" }}>
          Bitte öffne deinen Tisch-QR-Code erneut.
        </p>
      </div>
    );
  }

  const { guest_name, nickname, table_number, table_label, wedding_name } = session;

  return (
    <div style={styles.page}>
      {/* Live-Ankündigung */}
      {banner && (
        <AnnouncementBanner
          key={bannerKey}
          message={banner}
          onDismiss={() => setBanner(null)}
        />
      )}

      {/* ── Header ── */}
      <header style={styles.header}>
        <p style={styles.monogram}>{makeInitials(wedding_name)}</p>
        <h1 style={styles.display}>
          Hallo, <em style={{ color: "var(--accent)", fontStyle: "italic" }}>{nickname?.trim() || firstName(guest_name)}</em>
        </h1>
        <p style={{
          margin: "10px 0 0",
          fontSize: "1.05rem",
          color: "var(--ink-muted)",
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          lineHeight: 1.4,
        }}>
          Schön, dass du heute mit uns feierst! ✦
        </p>
        {table_number && (
          <div style={styles.tableBadge}>
            <span style={styles.eyebrow}>Tisch</span>
            <span style={styles.tableNum}>{table_label ? `${table_number} – ${table_label}` : table_number}</span>
          </div>
        )}
        {announcements[0] && (
          <div style={styles.announcementBar}>
            <span style={{ color: "var(--accent)", flexShrink: 0 }}>✦</span>
            <span style={{ fontSize: 13, lineHeight: 1.4 }}>{announcements[0].message}</span>
          </div>
        )}
        {branding && (
          <p style={{ textAlign: "center", fontSize: 12, opacity: 0.7, marginTop: 12 }}>
            {branding.logo_url && <img src={branding.logo_url} alt={branding.name || ""} style={{ maxHeight: 24, verticalAlign: "middle", marginRight: 6 }} />}
            präsentiert von unsereHochzeits.app
          </p>
        )}
      </header>

      {/* ── Kacheln ── */}
      <main style={styles.grid}>
        {TILES.filter((tile) => {
          // Premium-Kacheln (Musik, Getränke) nur bei bezahlter Hochzeit zeigen.
          if (tile.premium && !isPaid) return false;
          // Getränke zusätzlich nur wenn vom Brautpaar freigeschaltet.
          if (tile.id === "drinks" && !drinksEnabled) return false;
          return true;
        }).map((tile) => (
          <TileButton key={tile.id} tile={tile} onTap={() => navigate(tile.path)} />
        ))}
        {canAnnounce && (
          <TileButton
            tile={{
              id: "announce", label: "Ankündigung", hint: "Sag allen Gästen Bescheid",
              icon: "✦", color: "var(--accent-deep)",
              bg: "linear-gradient(145deg, #F3E9F0 0%, #E9DCE6 100%)",
              border: "var(--accent-soft)",
            }}
            onTap={() => navigate("/guest/announce")}
          />
        )}
      </main>

      {/* ── Footer ── */}
      <footer style={styles.footer}>
        <span style={styles.eyebrow}>unsereHochzeits.app</span>
      </footer>
    </div>
  );
}

// ── TileButton ────────────────────────────────────────────────────────

function TileButton({ tile, onTap }) {
  const [pressed, setPressed] = useState(false);

  return (
    <button
      onClick={onTap}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      aria-label={tile.label}
      style={{
        display:        "flex",
        flexDirection:  "column",
        justifyContent: "flex-end",
        padding:        "20px 18px 18px",
        background:     tile.bg,
        border:         `1.5px solid ${tile.border}`,
        borderRadius:   "var(--radius-lg)",
        cursor:         "pointer",
        textAlign:      "left",
        transform:      pressed ? "scale(0.96)" : "scale(1)",
        transition:     "transform 0.12s ease, box-shadow 0.15s ease",
        boxShadow:      pressed
          ? "none"
          : "0 2px 12px rgba(42,31,26,0.07), 0 1px 3px rgba(42,31,26,0.05)",
        minHeight:      140,
      }}
    >
      <span style={{ fontSize: 32, lineHeight: 1, marginBottom: 12, color: tile.color }}>
        {tile.icon}
      </span>
      <span style={{
        fontFamily:  "var(--font-display)",
        fontSize:    "1.35rem",
        fontWeight:  500,
        color:       "var(--ink)",
        lineHeight:  1.1,
        marginBottom: 4,
      }}>
        {tile.label}
      </span>
      <span style={{ fontSize: 12, color: "var(--ink-muted)", lineHeight: 1.3 }}>
        {tile.hint}
      </span>
    </button>
  );
}

// ── AnnouncementBanner ────────────────────────────────────────────────

function AnnouncementBanner({ message, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 8000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position:     "fixed",
        top:          16,
        left:         "50%",
        transform:    "translateX(-50%)",
        width:        "calc(100% - 32px)",
        maxWidth:     440,
        zIndex:       100,
        background:   "var(--accent)",
        color:        "#fff",
        borderRadius: "var(--radius-md)",
        padding:      "14px 16px",
        boxShadow:    "var(--shadow-lg)",
        display:      "flex",
        alignItems:   "flex-start",
        gap:          10,
        animation:    "wos-rise 0.3s ease",
      }}
    >
      <span style={{ flexShrink: 0 }}>✦</span>
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.4, flex: 1 }}>{message}</p>
      <button
        onClick={onDismiss}
        aria-label="Schließen"
        style={{
          flexShrink: 0, background: "transparent", border: "none",
          color: "rgba(255,255,255,0.7)", fontSize: 20, lineHeight: 1,
          cursor: "pointer", padding: 0,
        }}
      >×</button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight:      "100dvh",
    background:     "var(--bg)",
    display:        "flex",
    flexDirection:  "column",
    padding:        "env(safe-area-inset-top, 0) 0 env(safe-area-inset-bottom, 0)",
    fontFamily:     "var(--font-body)",
    color:          "var(--ink)",
    maxWidth:       480,
    margin:         "0 auto",
    width:          "100%",
  },
  shell: {
    minHeight: "100dvh", display: "flex", alignItems: "center",
    justifyContent: "center", padding: 24, fontFamily: "var(--font-body)",
  },
  header: {
    padding:    "28px 24px 16px",
    flexShrink: 0,
  },
  monogram: {
    fontFamily:    "var(--font-display)",
    fontStyle:     "italic",
    fontSize:      12,
    letterSpacing: "0.3em",
    textTransform: "uppercase",
    color:         "var(--accent)",
    marginBottom:  10,
  },
  display: {
    fontFamily: "var(--font-display)",
    fontSize:   "clamp(2rem, 8vw, 2.6rem)",
    fontWeight: 500,
    lineHeight: 1.05,
    margin:     0,
  },
  tableBadge: {
    display:      "inline-flex",
    alignItems:   "center",
    gap:          8,
    marginTop:    14,
    padding:      "7px 16px",
    background:   "var(--accent-soft)",
    border:       "1px solid var(--accent)",
    borderRadius: "999px",
  },
  eyebrow: {
    fontSize:      11,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color:         "var(--ink-subtle)",
    fontWeight:    500,
  },
  tableNum: {
    fontFamily: "var(--font-display)",
    fontSize:   "1.2rem",
    fontStyle:  "italic",
    color:      "var(--accent-deep)",
  },
  announcementBar: {
    display:      "flex",
    alignItems:   "flex-start",
    gap:          8,
    marginTop:    14,
    padding:      "10px 14px",
    background:   "var(--surface)",
    border:       "1px solid var(--line)",
    borderLeft:   "3px solid var(--accent)",
    borderRadius: "var(--radius-md)",
    color:        "var(--ink-muted)",
  },
  grid: {
    flex:                1,
    display:             "grid",
    gridTemplateColumns: "1fr 1fr",
    gap:                 12,
    padding:             "8px 24px 24px",
  },
  footer: {
    textAlign:   "center",
    padding:     "12px 24px 20px",
    flexShrink:  0,
  },
  meta: { fontSize: 14, color: "var(--ink-muted)" },
};

// ── Helpers ──────────────────────────────────────────────────────────

function firstName(name) {
  return name?.trim().split(/\s+/)[0] ?? "";
}

function makeInitials(name) {
  if (!name) return "✦";
  const m = name.match(/([A-ZÄÖÜ])[^&]*&\s*([A-ZÄÖÜ])/i);
  if (m) return `${m[1].toUpperCase()} ✦ ${m[2].toUpperCase()}`;
  return name.trim().split(/\s+/).map((w) => w[0]).join(" ").toUpperCase();
}
